// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useEffect, useState } from 'react';

/**
 * One queue in full — every job of a kind waiting for a worker or out on one.
 *
 * The fleet payload carries a couple of dozen recent jobs, enough for an activity feed and not
 * enough for a queue: twenty-four deep, it could show two of them. This asks the queue itself,
 * oldest first, which is the order a worker takes them in.
 */

export type BatchQueueJob = {
  id: string;
  /** planned | dispatched for an analysis job; queued for the others. */
  status: string;
  requestedAt: string;
  dispatchedAt: string | null;
  workerId: string | null;
  /** Course name for the course-scoped kinds; the ML job's own kind otherwise. */
  summary: string | null;
  /** Which job of the run this is (kind · provider · detector@model), for analysis jobs. */
  detail: string | null;
  /** What a planned job is waiting for — the acquisition source, when that is why. */
  waitingOn: string | null;
  /** The run this job belongs to, for the analysis kinds. */
  runId: string | null;
  attempt: number;
};

export type BatchQueueState =
  | { status: 'loading' }
  | { status: 'ready'; jobs: BatchQueueJob[]; count: number }
  | { status: 'forbidden' }
  | { status: 'failed' };

/** The state plus a way to read the queue again, after something changed it. */
export type BatchQueueView = BatchQueueState & { reload: () => void };

type Row = Record<string, unknown>;

const str = (row: Row, ...keys: string[]): string | null => {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === 'string' && value.length > 0) return value;
  }
  return null;
};

const num = (row: Row, ...keys: string[]): number | null => {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
  }
  return null;
};

const mapJob = (row: Row): BatchQueueJob | null => {
  const id = str(row, 'id', 'Id');
  const requestedAt = str(row, 'requestedAt', 'RequestedAt');
  if (!id || !requestedAt) return null;
  return {
    id,
    status: str(row, 'status', 'Status') ?? 'queued',
    requestedAt,
    dispatchedAt: str(row, 'dispatchedAt', 'DispatchedAt'),
    workerId: str(row, 'workerId', 'WorkerId'),
    summary: str(row, 'summary', 'Summary'),
    detail: str(row, 'detail', 'Detail'),
    waitingOn: str(row, 'waitingOn', 'WaitingOn'),
    runId: str(row, 'runId', 'RunId'),
    attempt: num(row, 'attempt', 'Attempt') ?? 1,
  };
};

/** Maps the BFF's `/api/geodata/batch/queue/{kind}` body — pulled out for direct unit testing. */
export const mapQueueResponse = (body: unknown): { jobs: BatchQueueJob[]; count: number } => {
  const root = (body ?? {}) as Row;
  const rows = root.jobs ?? root.Jobs;
  const jobs = (Array.isArray(rows) ? (rows as Row[]) : [])
    .map(mapJob)
    .filter((job): job is BatchQueueJob => job !== null);
  return { jobs, count: num(root, 'count', 'Count') ?? jobs.length };
};

/**
 * Reads one queue while `kind` is set; null keeps the window closed and asks for nothing.
 * Re-read on every open rather than polled: a queue window is opened to answer a question
 * now, and the page behind it refreshes on its own.
 */
export function useBatchQueue(
  apiBaseUrl: string,
  token: string | null | undefined,
  kind: string | null
): BatchQueueView {
  const [state, setState] = useState<BatchQueueState>({ status: 'loading' });
  // Bumped by reload(). Clearing the queue is the one thing that changes it from in here, and
  // the window has to show the result rather than the pile the operator just cleared.
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (!kind || !token) {
      return;
    }

    let cancelled = false;
    setState({ status: 'loading' });

    const load = async () => {
      try {
        const response = await fetch(
          `${apiBaseUrl}/api/geodata/batch/queue/${encodeURIComponent(kind)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (cancelled) return;

        if (response.status === 401 || response.status === 403) {
          setState({ status: 'forbidden' });
          return;
        }
        if (!response.ok) {
          setState({ status: 'failed' });
          return;
        }

        const { jobs, count } = mapQueueResponse(await response.json());
        if (!cancelled) setState({ status: 'ready', jobs, count });
      } catch {
        if (!cancelled) setState({ status: 'failed' });
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, token, kind, nonce]);

  return { ...state, reload: () => setNonce((n) => n + 1) };
}

/**
 * Clears a queue: every job of the kind that has not been answered, or one named job.
 *
 * Returns how many were cancelled, or null when the call failed — the caller decides what to
 * say. A job a worker already holds is cancelled too; it finishes on that worker and its answer
 * is dropped, which is the honest limit of reaching a machine outside the VPC.
 */
export const cancelBatchQueue = async (
  apiBaseUrl: string,
  token: string | null | undefined,
  kind: string,
  jobId?: string
): Promise<number | null> => {
  if (!token) return null;

  try {
    const query = jobId ? `?jobId=${encodeURIComponent(jobId)}` : '';
    const response = await fetch(
      `${apiBaseUrl}/api/geodata/batch/queue/${encodeURIComponent(kind)}/cancel${query}`,
      { method: 'POST', headers: { Authorization: `Bearer ${token}` } }
    );
    if (!response.ok) return null;

    const body = (await response.json()) as Row | null;
    return num((body ?? {}) as Row, 'cancelled', 'Cancelled') ?? 0;
  } catch {
    return null;
  }
};
